import apiClient, { baseApiClient } from './client';

export const APP_STORE_SYNC_TIMEOUT_MS = 180_000;

export interface AppStoreAppInfo {
  [key: string]: unknown;
}

export interface AppStoreVersionSummary {
  id?: string;
  version_string?: string;
  state?: string;
  [key: string]: unknown;
}

export interface AppStoreLocalizationSummary {
  id?: string;
  locale?: string;
  title?: string;
  [key: string]: unknown;
}

export interface AppStoreBuildSummary {
  id?: string;
  version?: string;
  uploaded_date?: string;
  [key: string]: unknown;
}

export interface AppStoreReviewDetail {
  [key: string]: unknown;
}

export interface AppStoreMediaSummary {
  fetched_from_apple: boolean;
  [key: string]: unknown;
}

export interface AppStoreIapSubscriptionSummary {
  [key: string]: unknown;
}

export interface AppStorePriceAvailabilitySummary {
  [key: string]: unknown;
}

export interface AppStoreSnapshotResponse {
  app_key: string;
  platform: 'ios';
  bundle_id: string;
  asc_app_id: string | null;
  snapshot_revision: number;
  spec_version: string;
  spec_hash: string;
  app_info: AppStoreAppInfo;
  versions: AppStoreVersionSummary[];
  localizations: AppStoreLocalizationSummary[];
  builds: AppStoreBuildSummary[];
  review_detail: AppStoreReviewDetail;
  media_summary: AppStoreMediaSummary;
  iap_subscription_summary: AppStoreIapSubscriptionSummary;
  price_availability_summary: AppStorePriceAvailabilitySummary;
}

export interface AppStoreLatestSnapshotSummaryResponse {
  snapshot_id: string;
  snapshot_revision: number;
  spec_version: string;
  spec_hash: string;
  created_at?: string | null;
  version_count: number;
  build_count: number;
}

export interface AppStoreAppCatalogItemResponse {
  id: string;
  app_key: string;
  platform: 'ios';
  bundle_id: string;
  asc_app_id: string | null;
  display_name?: string | null;
  sku?: string | null;
  last_synced_at?: string | null;
  latest_snapshot?: AppStoreLatestSnapshotSummaryResponse | null;
}

export interface AppStoreAppCatalogResponse {
  items: AppStoreAppCatalogItemResponse[];
  count: number;
}

export interface AppStoreSpecContractResponse {
  snapshot_revision: number | null;
  spec_version: string;
  spec_hash: string;
  spec: Record<string, unknown>;
}

export type AppStoreDraftSource = 'manual' | 'agent' | 'doractl' | 'aso_import';
export type AppStoreDraftState = 'draft' | 'approved' | 'archived';

export interface AppStoreDraftCreateRequest {
  snapshot_id?: string | null;
  base_snapshot_revision: number;
  spec_hash: string;
  version_string: string;
  source: AppStoreDraftSource;
  reason?: string | null;
  draft_payload: Record<string, unknown>;
}

export interface AppStoreDraftPatchRequest {
  draft_revision_id: string;
  base_snapshot_revision: number;
  spec_hash: string;
  source: AppStoreDraftSource;
  reason?: string | null;
  patch_payload: Record<string, unknown>;
}

export interface AppStoreTargetImportEvidenceRequest {
  rating: number;
  reviews: number;
  features: string[];
}

export interface AppStoreAsoImportRequest {
  draft_revision_id: string;
  base_snapshot_revision: number;
  spec_hash: string;
  source_path: string;
  reason?: string | null;
  target_evidence?: AppStoreTargetImportEvidenceRequest | null;
}

export interface AppStoreDraftScreenshotUploadRequest {
  draft_revision_id: string;
  base_snapshot_revision: number;
  spec_hash: string;
  locale: string;
  display_type: string;
  slot?: string | null;
  order?: number | null;
  replace_index?: number | null;
  reason?: string | null;
  file: File;
}

export interface AppStoreDraftScreenshotAttachRequest {
  draft_revision_id: string;
  base_snapshot_revision: number;
  spec_hash: string;
  locale: string;
  display_type: string;
  object_name?: string | null;
  file_id?: string | null;
  slot?: string | null;
  order?: number | null;
  replace_index?: number | null;
  reason?: string | null;
}

export interface AppStoreUserImageFileResponse {
  id: string;
  url: string;
  object_name: string;
  filename: string;
  size: number;
  content_type: string;
}

export interface AppStoreUserImageFileListResponse {
  files: AppStoreUserImageFileResponse[];
  total: number;
}

export interface AppStoreDraftResponse {
  id: string;
  app_id: string;
  app_key: string;
  snapshot_id: string;
  base_snapshot_revision: number;
  spec_version: string;
  spec_hash: string;
  version_string: string;
  state: AppStoreDraftState;
  current_revision_id: string;
  revision_number: number;
  draft_payload: Record<string, unknown>;
  source: AppStoreDraftSource;
  reason?: string | null;
  actor: string;
  created_by: string;
  updated_by: string;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AppStoreDiffItemResponse {
  category: string;
  path: string;
  before: unknown;
  after: unknown;
  change_type: string;
  risk: string;
  message: string;
}

export interface AppStoreDiffResponse {
  items: AppStoreDiffItemResponse[];
}

export interface AppStoreValidationTargetGateEvidenceRequest {
  rating?: number | null;
  review_count?: number | null;
  features: string[];
}

export interface AppStoreDraftValidateRequest {
  draft_revision_id?: string | null;
  base_payload?: Record<string, unknown> | null;
  app_state?: string | null;
  version_state?: string | null;
  shipped_features: string[];
  visual_proof_features: string[];
  target_gate_evidence?: AppStoreValidationTargetGateEvidenceRequest | null;
  promotional_text_change_reason?: string | null;
  supported_features: string[];
}

export interface AppStoreValidationItemResponse {
  severity: 'blocking' | 'warning' | 'info';
  rule_id: string;
  field_path: string;
  message: string;
  evidence: Record<string, unknown>;
}

export interface AppStoreValidationResponse {
  draft_revision_id: string;
  revision_number: number;
  has_blocking_errors: boolean;
  items: AppStoreValidationItemResponse[];
}

export type AppStoreReleaseMode = 'manual' | 'automatic';

export interface AppStoreReleaseApprovalRequest {
  draft_revision_id: string;
  validation_run_id?: string | null;
  approved_hash: string;
  build_id: string;
  release_mode: AppStoreReleaseMode;
  review_notes?: string | null;
}

export interface AppStoreReleaseApprovalInfoRequest {
  draft_revision_id: string;
  validation_run_id?: string | null;
  build_id: string;
  release_mode: AppStoreReleaseMode;
  review_notes?: string | null;
}

export interface AppStoreReleaseApprovalInfoResponse {
  draft_id: string;
  draft_revision_id: string;
  validation_run_id?: string | null;
  app_key: string;
  snapshot_id: string;
  snapshot_revision: number;
  spec_version: string;
  spec_hash: string;
  version_string: string;
  build_id: string;
  release_mode: AppStoreReleaseMode;
  review_notes?: string | null;
  approved_hash: string;
}

export interface AppStoreReleaseApprovalResponse {
  id: string;
  app_key: string;
  draft_id: string;
  draft_revision_id: string;
  snapshot_id: string;
  snapshot_revision: number;
  spec_version: string;
  spec_hash: string;
  version_string: string;
  build_id: string;
  release_mode: AppStoreReleaseMode;
  approved_hash: string;
  state: string;
  approved_by: string;
  created_at?: string | null;
  payload: Record<string, unknown>;
}

export interface AppStoreReleaseSubmitRequest {
  approved_snapshot_hash: string;
  build_id: string;
  release_mode: AppStoreReleaseMode;
  review_notes?: string | null;
}

export interface AppStoreReleaseSubmissionEvent {
  state?: string;
  message?: string;
  created_at?: string | null;
  [key: string]: unknown;
}

export interface AppStoreReleaseSubmissionResponse {
  id: string;
  app_key: string;
  approved_snapshot_id: string;
  approved_snapshot_hash: string;
  build_id: string;
  release_mode: AppStoreReleaseMode;
  state: string;
  asc_app_store_version_id?: string | null;
  asc_review_submission_id?: string | null;
  asc_review_submission_item_id?: string | null;
  last_error?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  submitted_at?: string | null;
  last_polled_at?: string | null;
  events: AppStoreReleaseSubmissionEvent[];
}

export interface AppStoreReleaseVerifyPublicStoreResponse {
  submission_id: string;
  bundle_id: string;
  expected_version: string;
  storefront: string;
  public_version?: string | null;
  track_view_url?: string | null;
  is_released: boolean;
  checked_at?: string | null;
  evidence: Record<string, unknown>;
}

export const appStoreReleasesApi = {
  listApps: async (): Promise<AppStoreAppCatalogResponse> => {
    const response = await apiClient.get<AppStoreAppCatalogResponse>(
      '/app-store-releases/apps',
    );
    return response.data;
  },

  discoverApps: async (): Promise<AppStoreAppCatalogResponse> => {
    const response = await apiClient.post<AppStoreAppCatalogResponse>(
      '/app-store-releases/apps/discover',
    );
    return response.data;
  },

  syncApp: async (appKey: string): Promise<AppStoreSnapshotResponse> => {
    const response = await apiClient.post<AppStoreSnapshotResponse>(
      `/app-store-releases/apps/${appKey}/sync`,
      undefined,
      { timeout: APP_STORE_SYNC_TIMEOUT_MS },
    );
    return response.data;
  },

  getSnapshot: async (appKey: string): Promise<AppStoreSnapshotResponse> => {
    const response = await apiClient.get<AppStoreSnapshotResponse>(
      `/app-store-releases/apps/${appKey}/snapshot`,
    );
    return response.data;
  },

  getSpec: async (specVersion: string): Promise<AppStoreSpecContractResponse> => {
    const response = await apiClient.get<AppStoreSpecContractResponse>(
      `/app-store-releases/specs/${specVersion}`,
    );
    return response.data;
  },

  createDraft: async (
    appKey: string,
    request: AppStoreDraftCreateRequest,
  ): Promise<AppStoreDraftResponse> => {
    const response = await apiClient.post<AppStoreDraftResponse>(
      `/app-store-releases/apps/${appKey}/drafts`,
      request,
    );
    return response.data;
  },

  patchDraft: async (
    draftId: string,
    request: AppStoreDraftPatchRequest,
  ): Promise<AppStoreDraftResponse> => {
    const response = await apiClient.patch<AppStoreDraftResponse>(
      `/app-store-releases/drafts/${draftId}`,
      request,
    );
    return response.data;
  },

  importAso: async (
    draftId: string,
    request: AppStoreAsoImportRequest,
  ): Promise<AppStoreDraftResponse> => {
    const response = await apiClient.post<AppStoreDraftResponse>(
      `/app-store-releases/drafts/${draftId}/import-aso`,
      request,
    );
    return response.data;
  },

  uploadDraftScreenshot: async (
    draftId: string,
    request: AppStoreDraftScreenshotUploadRequest,
  ): Promise<AppStoreDraftResponse> => {
    const form = new FormData();
    form.append('draft_revision_id', request.draft_revision_id);
    form.append('base_snapshot_revision', String(request.base_snapshot_revision));
    form.append('spec_hash', request.spec_hash);
    form.append('locale', request.locale);
    form.append('display_type', request.display_type);
    if (request.slot) {
      form.append('slot', request.slot);
    }
    if (request.order !== null && request.order !== undefined) {
      form.append('order', String(request.order));
    }
    if (request.replace_index !== null && request.replace_index !== undefined) {
      form.append('replace_index', String(request.replace_index));
    }
    if (request.reason) {
      form.append('reason', request.reason);
    }
    form.append('file', request.file);

    const response = await apiClient.post<AppStoreDraftResponse>(
      `/app-store-releases/drafts/${draftId}/media/screenshots`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  attachDraftScreenshot: async (
    draftId: string,
    request: AppStoreDraftScreenshotAttachRequest,
  ): Promise<AppStoreDraftResponse> => {
    const response = await apiClient.post<AppStoreDraftResponse>(
      `/app-store-releases/drafts/${draftId}/media/screenshots/attach`,
      request,
    );
    return response.data;
  },

  listUserImageFiles: async (limit = 50): Promise<AppStoreUserImageFileListResponse> => {
    const response = await baseApiClient.get<AppStoreUserImageFileListResponse>(
      '/v1/files/list',
      { params: { limit } },
    );
    return response.data;
  },

  getDiff: async (draftId: string): Promise<AppStoreDiffResponse> => {
    const response = await apiClient.get<AppStoreDiffResponse>(
      `/app-store-releases/drafts/${draftId}/diff`,
    );
    return response.data;
  },

  validateDraft: async (
    draftId: string,
    request: AppStoreDraftValidateRequest,
  ): Promise<AppStoreValidationResponse> => {
    const response = await apiClient.post<AppStoreValidationResponse>(
      `/app-store-releases/drafts/${draftId}/validate`,
      request,
    );
    return response.data;
  },

  getApprovalInfo: async (
    draftId: string,
    request: AppStoreReleaseApprovalInfoRequest,
  ): Promise<AppStoreReleaseApprovalInfoResponse> => {
    const response = await apiClient.post<AppStoreReleaseApprovalInfoResponse>(
      `/app-store-releases/drafts/${draftId}/approve-info`,
      request,
    );
    return response.data;
  },

  approveDraft: async (
    draftId: string,
    request: AppStoreReleaseApprovalRequest,
  ): Promise<AppStoreReleaseApprovalResponse> => {
    const response = await apiClient.post<AppStoreReleaseApprovalResponse>(
      `/app-store-releases/drafts/${draftId}/approve`,
      request,
    );
    return response.data;
  },

  submitApprovedSnapshot: async (
    snapshotId: string,
    request: AppStoreReleaseSubmitRequest,
  ): Promise<AppStoreReleaseSubmissionResponse> => {
    const response = await apiClient.post<AppStoreReleaseSubmissionResponse>(
      `/app-store-releases/approved-snapshots/${snapshotId}/submit`,
      request,
    );
    return response.data;
  },

  getSubmission: async (submissionId: string): Promise<AppStoreReleaseSubmissionResponse> => {
    const response = await apiClient.get<AppStoreReleaseSubmissionResponse>(
      `/app-store-releases/submissions/${submissionId}`,
    );
    return response.data;
  },

  pollSubmission: async (submissionId: string): Promise<AppStoreReleaseSubmissionResponse> => {
    const response = await apiClient.post<AppStoreReleaseSubmissionResponse>(
      `/app-store-releases/submissions/${submissionId}/poll`,
    );
    return response.data;
  },

  verifyPublicStore: async (submissionId: string): Promise<AppStoreReleaseVerifyPublicStoreResponse> => {
    const response = await apiClient.post<AppStoreReleaseVerifyPublicStoreResponse>(
      `/app-store-releases/submissions/${submissionId}/verify-public-store`,
    );
    return response.data;
  },
};
